import { useEffect, useMemo } from "react";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { addDays } from "@shared/dates";
import { todayIso } from "@shared/ids";
import { useMe } from "./session";

export const SCHEDULE_RANGES = [
  { key: "upcoming", label: "Upcoming" }, { key: "today", label: "Today" }, { key: "7d", label: "7 days" },
  { key: "30d", label: "30 days" }, { key: "custom", label: "Custom" }, { key: "all", label: "All time" },
] as const;
type Range = typeof SCHEDULE_RANGES[number]["key"];
export type ScheduleSearch = { range?: Range; venue?: string; person?: string; status?: string; q?: string; from?: string; to?: string };

/** Whitelist the shareable URL contract; unknown data and oversized persisted values are ignored. */
export function parseScheduleSearch(raw: Record<string, unknown>): ScheduleSearch {
  const out: ScheduleSearch = {};
  if (SCHEDULE_RANGES.some((r) => r.key === raw.range)) out.range = raw.range as Range;
  for (const key of ["venue", "person", "status", "q", "from", "to"] as const) if (typeof raw[key] === "string" && raw[key].trim() && raw[key].length <= (key === "venue" ? 1300 : 200)) out[key] = key === "q" ? raw[key] : raw[key].trim();
  return out;
}

export function scheduleDates(filters: ScheduleSearch, timezone: string | null, now = new Date()): { from?: string; to?: string; all?: "1" } {
  const today = todayIso(timezone || "America/Chicago", now);
  switch (filters.range) {
    case "all": return { all: "1" };
    case "custom": return { from: filters.from, to: filters.to, all: !filters.from && !filters.to ? "1" : undefined };
    case "today": return { from: today, to: today };
    case "7d": return { from: today, to: addDays(today, 6) };
    case "30d": return { from: today, to: addDays(today, 29) };
    default: return { from: today };
  }
}

export function useScheduleFilters(kind: "tours" | "events") {
  const me = useMe(); const navigate = useNavigate();
  const search = useSearch({ strict: false }) as ScheduleSearch;
  const key = `v365:${me.organization.id}:${me.user.id}:${kind}:filters`;
  const defaults: ScheduleSearch = { range: "upcoming", status: kind === "tours" ? "scheduled" : "all" };
  const stored = useMemo(() => {
    try { const raw: unknown = JSON.parse(localStorage.getItem(key) || "{}"); return raw && typeof raw === "object" && !Array.isArray(raw) ? parseScheduleSearch(raw as Record<string, unknown>) : {}; }
    catch { return {}; }
  }, [key]);
  const hasURL = Object.keys(search).length > 0;
  const filters = { ...defaults, ...(hasURL ? search : stored) };
  const serialized = JSON.stringify(filters);
  useEffect(() => {
    if (!hasURL) void navigate({ to: kind === "tours" ? "/tours" : "/events", search: filters, replace: true });
    try { localStorage.setItem(key, serialized); } catch { /* Private/disabled storage: the URL still works. */ }
  }, [hasURL, key, serialized, kind, navigate]);
  const update = (patch: ScheduleSearch, replace = false) => navigate({ to: kind === "tours" ? "/tours" : "/events", search: parseScheduleSearch({ ...filters, ...patch }), replace });
  const reset = () => navigate({ to: kind === "tours" ? "/tours" : "/events", search: defaults });
  return { filters, update, reset };
}
